import styled from '@emotion/styled'
import { White } from '@mehilainen/mds-customer/colors'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { LocationResult, PublicNode } from '../../../__generated__/api'
import AppointmentListToggle, {
  AppointmentListVariant,
} from '../../../common/components/AppointmentList/AppointmentListToggle'
import Breadcrumb from '../../../common/components/Breadcrumb/Breadcrumb'
import { MinMaxDiv, VisuallyHidden } from '../../../common/components/Layout/Layout'
import { Text } from '../../../common/components/Typography/Typography'
import { useIsDesktop } from '../../../common/hooks/useBreakpoint'
import { useOHC } from '../../../common/hooks/useOHC'
import { maxWidth } from '../../../common/utils/breakpoint'
import { isFromAppAtom } from '../../../state/common/atoms'
import { useSearchEvents } from '../../../xstate/events'
import { useBreadCrumb, useSearchTarget, useSelectedPractitioner } from '../../../xstate/selectors'
import { SearchTargetValue } from '../../../xstate/types'

import SearchFilterChips from './SearchFilterChips'
import SearchLocationSelect from './SearchLocationSelect'
import SearchPractitionerServiceSelect from './SearchPractitionerServiceSelect'
import SearchServiceSelect from './SearchServiceSelect'
import { PatternContainer, ResponsiveContainer } from './SearchViewContainers'

interface Props {
  selectedNode: PublicNode | null
  locations: LocationResult[]
  appointmentListVariant: AppointmentListVariant
  setAppointmentListVariant(variant: AppointmentListVariant): void
}

const BreadcrumbContainer = styled.div`
  margin-top: 16px;
  margin-bottom: -8px;
`

const SearchHeader = React.forwardRef<HTMLDivElement, Props>(
  ({ selectedNode, locations, appointmentListVariant, setAppointmentListVariant }, ref) => {
    const isDesktop = useIsDesktop()
    const isFromApp = useRecoilValue(isFromAppAtom)
    const searchTarget = useSearchTarget()
    const { setSearchTarget } = useSearchEvents()
    const breadcrumb = useBreadCrumb()
    const selectedPractitioner = useSelectedPractitioner()
    const { t } = useTranslation()
    const { isOHCSide } = useOHC()

    const breadcrumbPath = useMemo(() => {
      if (breadcrumb.length <= 2) {
        return [breadcrumb[0]]
      } else {
        return breadcrumb.slice(0, 2)
      }
    }, [breadcrumb])

    return (
      <>
        <PatternContainer ref={ref} isFromApp={isFromApp}>
          <MinMaxDiv size={`${maxWidth.content}px`} gap={!isDesktop ? '16px' : undefined}>
            <VisuallyHidden>
              <Text as="h1" $size={450} $color={White}>
                {t('component.searchView.heading')}
              </Text>
            </VisuallyHidden>
            <BreadcrumbContainer>
              <Breadcrumb
                path={breadcrumbPath}
                color={White}
                onSelect={(selectedPath) => setSearchTarget(selectedPath.searchTarget)}
              />
            </BreadcrumbContainer>
            <ResponsiveContainer>
              <SearchServiceSelect />
              {selectedPractitioner && (
                <SearchPractitionerServiceSelect practitionerDetails={selectedPractitioner} />
              )}
              <SearchLocationSelect
                locations={locations}
                selectedPractitioner={selectedPractitioner}
                selectedNode={selectedNode}
              />
            </ResponsiveContainer>
            <SearchFilterChips hideInsuranceButton={isOHCSide} />
          </MinMaxDiv>
        </PatternContainer>
        {searchTarget.value === SearchTargetValue.Node && (
          <MinMaxDiv size={`${maxWidth.content}px`}>
            <AppointmentListToggle
              value={appointmentListVariant}
              onToggle={setAppointmentListVariant}
            />
          </MinMaxDiv>
        )}
      </>
    )
  }
)

export default SearchHeader
